import { themeGet } from './index';

/**
 * A data type that represents the individual RGB color components for a color.
 */
export type RGB = { r: number; g: number; b: number; a?: number };

/**
 * Gets the color of a palette shade using props.color as the palette color. If palette shade
 * does not exist, falls back to theme.colors.
 *
 * @example getPaletteColor('dark')(props) => returns the dark shade of the theme.palette[props.color].dark
 * @example getPaletteColor('primary.base')(props) => theme.palette.primary.base
 * @example getPaletteColor('primary', 'base')(props) => theme.palette.primary.base
 * @param args
 */
export const getPaletteColor = (...args) => (props: any): string => {
    let color = args.length === 2 ? args[0] : props.color || args[0];
    let shade = args.length === 2 ? args[1] : args[0];

    const colorShade = shade.match(/^([a-z]+)\.([a-z]+)$/);

    if (colorShade) {
        color = colorShade[0];
        shade = colorShade[1];
    }

    return (
        themeGet(`palette.${color}.${shade}`)(props)
        || themeGet(`palette.${color}`)(props)
        || themeGet(`colors.${color}`)(props)
        || color
    );
};

/**
 * Converts the specified hex color string to an RGB object.
 *
 * @param {string} hex
 * @returns {RGB}
 */
export const toRGB = (hex: string): RGB => {
    hex = hex.replace(/^#/, '');

    const color: RGB = { r: 0, g: 0, b: 0 }; // eslint-disable-line object-curly-newline

    if (hex.length === 6) {
        color.r = parseInt(hex.substring(0, 2), 16);
        color.g = parseInt(hex.substring(2, 4), 16);
        color.b = parseInt(hex.substring(4), 16);
    }

    return color;
};

/**
 * Returns the luminance of the specified color.
 *
 * The value for `color` can either be a hex color string, `#ffcc00`, or an RGB object,
 * `{ r: 100, g: 150, b: 50 }`.
 *
 * @param {string | RGB} color
 * @returns {number}
 */
export const luminance = (color: any): number => {
    const rgb = toRGB(color);
    if (!rgb) return null;
    return 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
};

/**
 * Returns whether the specified color is light based on its luminance.
 *
 * The value for `color` can either be a hex color string, `#ffcc00`, or an RGB object,
 * `{ r: 100, g: 150, b: 50 }`.
 *
 * @param {string | RGB} color
 * @returns {boolean}
 */
export const isLight = (color: string | RGB): boolean => {
    const lum = luminance(color);
    return lum && lum >= 140;
};

export const createColorScale = (colors: string[]): any => (colors || []).reduce((result: any, color, index) => {
    const indexKey = index === 0 ? '50' : `${index * 100}`;
    result[indexKey] = color;
    return result;
}, {});

export const colorForValue = (ramp: { [key: string]: string }, value: number): string => {
    let color: string = null;

    Object.keys(ramp).forEach((key) => {
        const v = parseFloat(key);
        if (v <= value) {
            color = ramp[key];
        }
    });

    return color;
};

type ColorStop = {
    value: number;
    color: string;
    pos: number;
};

export const colorsInRange = (
    ramp: { [key: string]: string },
    from: number,
    to: number,
    interval: number = 1,
    total: number = null
): ColorStop[] => {
    const min = Math.floor(from / interval) * interval;
    const max = Math.floor(to / interval) * interval;
    const delta = to - from;
    let colors: ColorStop[] = [];

    const sortedKeys = Object.keys(ramp)
        .map((el) => parseFloat(el))
        .sort((a, b) => a - b);

    let lastInRange: ColorStop;
    sortedKeys.forEach((key, index) => {
        const color = ramp[key];
        const value = key;
        if (value >= min && value <= max) {
            let pos = delta > 0 ? (value - from) / delta : 0;
            if (Number.isNaN(pos)) {
                pos = 0;
            }
            colors.push({ color, pos, value });
        }
        if (value <= max) {
            lastInRange = { color, pos: 0, value };
        }
    });

    // if no colors in range for the given interval, determine if range is out of bounds and set
    // color accordingly, or use the last color assigned below max value
    if (colors.length === 0) {
        if (sortedKeys.length > 0) {
            const first = sortedKeys[0];
            const last = sortedKeys[sortedKeys.length - 1];
            if (max < first) {
                colors.push({ color: ramp[first], pos: 0, value: first });
            } else if (min > last) {
                colors.push({ color: ramp[last], pos: 0, value: last });
            }
        }
    }

    if (colors.length === 0 && undefined !== lastInRange) {
        colors.push(lastInRange);
    }

    // if we only have a single color stop, just duplicate it for the last position
    if (colors.length === 1) {
        colors.push({ color: colors[0].color, pos: 1.0, value: colors[0].value });
    } else if (total > 1 && colors.length > total) {
        const int = total - 1;
        // pick n elements from a, distibuted evenly based on `total`
        const p = Math.floor(colors.length / int);
        const reduced = colors.slice(0, p * int).filter((_, i) => i % p === 0);
        // ensure last value is always included
        colors = reduced.concat(colors[colors.length - 1]);
    }

    return colors;
};
